import * as mediaServer from 'Components/utils/media-server';
import { defaultDictionnary } from 'Components/utils/media-server/dictionary';
import { generateUniqueId } from 'Utils/unique-id/uniqueId';
export type HeroSize = 'small' | 'medium' | 'large' | 'highlight' | 'highlightSmall' | string;
type Params = {
  id: string;
  url: string;
  width1x: number;
  width2x: number;
};
type GetUrlFunction = (url: string, width: number) => string;
type GetRuleFunction = (
  params: Params,
) => (getUrlFunction: GetUrlFunction) => (prefixes: Array<string>) => string;
const AUTO = '_AUTOFORMAT_' as const;
const PREFIXES = ['webkit', 'moz', 'o', 'ms'];

const getBackgroundImageUrl = (url: string, width: number): string => {
  const options = {
    format: AUTO,
    url,
    width,
    height: 'auto',
    transform: 'resize' as const,
    quality: 70,
  };
  return mediaServer.getUrl(options);
};

const getBackgroundRule = (params: Params) => {
  return (getUrlFunction: GetUrlFunction) => {
    return (prefixes: Array<string>) => {
      const { id, url, width1x, width2x } = params;
      return `.${id} {
        background-image:url(${getUrlFunction(url, width1x)});
        ${prefixes.reduce(
          (acc, prefix) => `${acc}
        background-image: -${prefix}-image-set(url(${getUrlFunction(url, width1x)}) 1x, url(${getUrlFunction(url, width2x)}) 2x);
        `,
          '',
        )}
      }`;
    };
  };
};

const getAllBackgroundRules = (params: Params) => {
  return (getRuleFunction: GetRuleFunction) => {
    return `${getRuleFunction(params)(getBackgroundImageUrl)(PREFIXES)}`;
  };
};

export const ICON_POSITIONS = {
  'top-left': 'items-start justify-start',
  'top-middle': 'items-start justify-center',
  'top-right': 'items-start justify-end',
  'center-left': 'items-center justify-start',
  'center-middle': 'items-center justify-center',
  'center-right': 'items-center justify-end',
  'bottom-left': 'items-end justify-start',
  'bottom-middle': 'items-end justify-center',
  'bottom-right': 'items-end justify-end',
};
export const LINK_SPACINGS = {
  left: 'md:p-30 md:-ms-30',
  middle: 'md:p-30',
  right: 'md:p-30 md:-me-30',
};
export const TITLE_POSITIONS = {
  'top-left': 'mb-auto items-start text-start',
  'top-middle': 'mb-auto items-center text-center',
  'top-right': 'mb-auto items-end text-end',
  'center-left': 'my-auto items-start text-start',
  'center-middle': 'my-auto items-center text-center',
  'center-right': 'my-auto items-end text-end',
  'bottom-left': 'mt-auto items-start text-start',
  'bottom-middle': 'mt-auto items-center text-center',
  'bottom-right': 'mt-auto items-end text-end',
};
export const SIZES = {
  small: {
    mobile: {
      height: '220px',
      paddingBottom: '60px',
    },
    desktop: {
      height: '340px',
      paddingBottom: '80px',
    },
  },
  highlightSmall: {
    mobile: {
      height: '220px',
      paddingBottom: '60px',
    },
    desktop: {
      height: '340px',
      paddingBottom: '80px',
    },
  },
  medium: {
    mobile: {
      height: '320px',
      paddingBottom: '60px',
    },
    desktop: {
      height: '440px',
      paddingBottom: '100px',
    },
  },
  large: {
    mobile: {
      height: '420px',
      paddingBottom: '60px',
    },
    desktop: {
      height: '540px',
      paddingBottom: '115px',
    },
  },
  highlight: {
    mobile: {
      height: '480px',
      paddingBottom: '40px',
    },
    desktop: {
      height: '480px',
      paddingBottom: '70px',
    },
  },
  full: {
    mobile: {
      height: '100%',
      paddingBottom: '60px',
    },
    desktop: {
      height: '100%',
      paddingBottom: '115px',
    },
  },
};
export const getHeroDimensions = (sizeProp: HeroSize) => {
  const isHighlight = sizeProp === 'highlightSmall' || sizeProp === 'highlight';
  return {
    heightClassName: `immersiveHeight--${sizeProp}`,
    paddingBottomClassName: `immersivePaddingBottom--${sizeProp}`,
    paddingTopClassName: isHighlight ? 'immersivePaddingTop--highlight' : 'immersivePaddingTop',
  };
};
export const generateBackgroundStylesAndClassName = ({
  backgroundURLDesktop,
  backgroundURLMobile,
}: {
  backgroundURLDesktop: string;
  backgroundURLMobile: string;
}) => {
  if (!backgroundURLDesktop && !backgroundURLMobile) {
    return {
      style: '',
      className: '',
    };
  }

  const url = backgroundURLDesktop || backgroundURLMobile;
  const componentId = generateUniqueId([url]);
  const widths = defaultDictionnary.getWidthRanges();
  const params = {
    id: componentId,
    url,
  };
  const mobileUrl = backgroundURLMobile || backgroundURLDesktop;
  return {
    className: componentId,
    style: `${getAllBackgroundRules({
      ...params,
      url: mobileUrl,
      width1x: widths[1],
      width2x: widths[2],
    })(getBackgroundRule)}
      @media all and (min-width: 22.51em) {
        ${getAllBackgroundRules({
          ...params,
          url: mobileUrl,
          width1x: widths[2],
          width2x: widths[5],
        })(getBackgroundRule)}
      }
      @media all and (min-width: 40.01em) {
        ${getAllBackgroundRules({ ...params, width1x: widths[3], width2x: widths[5] })(
          getBackgroundRule,
        )}
      }
      @media all and (min-width: 64.01em) {
        ${getAllBackgroundRules({ ...params, width1x: widths[5], width2x: widths[6] })(
          getBackgroundRule,
        )}
      }
      @media all and (min-width: 76.26em) {
        ${getAllBackgroundRules({ ...params, width1x: widths[6], width2x: widths[6] })(
          getBackgroundRule,
        )}
      }`,
  };
};
export { getBackgroundImageUrl, getBackgroundRule, getAllBackgroundRules, AUTO, PREFIXES };
